// Top bar / header — used on all pages. Shows app title and (later) currency / sync state.
// Phase 74: search/theme-toggle buttons removed (cleaner chrome). Cmd/Ctrl+K
// still opens the global search palette; theme toggle remains available
// in the desktop Sidebar.
// Phase 75: FilterBar merged into the header body — one unified bar
// instead of a header + bordered sub-bar.
// Phase 76: header is a SINGLE LINE at all viewports — logo, FilterBar
// (segments + period nav), and the page's right cluster all share one
// row. The category chip row is dropped from the header; categories now
// live inside the filter drawer as multi-select (Phase 76b).
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/index.jsx';
import { useTopBarRightSlotSetter } from '../lib/topbar-slot.jsx';
import FilterBar from './FilterBar.jsx';

export default function TopBar({ title, onSearchClick, categories, right }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  // Phase 94: register the right-cluster slot ref via Context so
  // consumers (Transactions, Dashboard, etc.) can portal content into
  // the TopBar without racing with this component's commit. Solves the
  // Phase 92 createPortal crash on /transactions.
  const setSlot = useTopBarRightSlotSetter();
  const slotRef = useRef(null);
  useEffect(() => {
    if (setSlot && slotRef.current) {
      setSlot(slotRef.current);
    }
    return () => {
      if (setSlot) setSlot(null);
    };
  }, [setSlot]);
  // Phase 74b: search tooltip still pops on Cmd/Ctrl+K keystroke — but
  // only when the page mounts the CommandPalette. The button is gone,
  // but the keyboard affordance survives.
  const [showTip, setShowTip] = useState(false);
  // Track Cmd/Ctrl+K so we can show the tip toast from anywhere.
  React.useEffect(() => {
    if (!onSearchClick) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        setShowTip(true);
        setTimeout(() => setShowTip(false), 1800);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSearchClick]);
  return (
    <header className="sticky top-0 z-20 bg-white/95 dark:bg-slate-800/95 backdrop-blur border-b border-slate-200 dark:border-slate-700">
      {/* Phase 76: single-line header. Brand | FilterBar (segments + period
          nav) | right cluster (篩選 button from Dashboard, quickAdd, etc.).
          On mobile: drop the page title + wordmark text so the row fits in
          390px; desktop keeps them. */}
      <div
        className="max-w-3xl mx-auto px-2 sm:px-4 h-12 flex items-center gap-1.5 sm:gap-2"
        data-testid="topbar-row"
      >
        <Link
          to="/"
          className="flex items-center gap-1 text-brand-600 dark:text-brand-400 font-semibold shrink-0"
        >
          <span className="text-base hidden sm:inline">Finance</span>
        </Link>
        {/* Page title (desktop only — mobile users have BottomNav). */}
        {title && (
          <h1 className="hidden md:block text-sm font-medium text-slate-700 dark:text-slate-200 truncate shrink min-w-0">
            {title}
          </h1>
        )}
        {/* Inline FilterBar — segments + period nav only. Category chips
            moved to the filter drawer (Phase 76b). */}
        <div className="flex-1 min-w-0 overflow-hidden" data-testid="filter-bar-slot">
          <FilterBar categories={categories || []} compact />
        </div>
        {/* Right cluster — page-provided (篩選 button, quick-add, etc.)
            Phase 76b: portal slot for Dashboard's篩選 button + active-count
            badge. Mounted empty; pages can portal content into it via
            `document.getElementById('topbar-right-slot')`. */}
        <div className="flex items-center gap-1 shrink-0">
          <div id="topbar-right-slot" ref={slotRef} data-testid="topbar-right-slot" className="contents" />
          <button
            data-testid="topbar-quickadd"
            onClick={() => navigate('/add')}
            className="hidden md:inline-flex items-center gap-1 px-2.5 h-8 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium"
            aria-label={t('topbar.quickAdd')}
            title={t('topbar.quickAdd')}
          >
            <span aria-hidden="true">＋</span>
            <span>{t('topbar.quickAdd')}</span>
          </button>
        </div>
      </div>
      {showTip && (
        <div className="absolute right-4 top-12 mt-1 bg-slate-900 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none z-30">
          {t('topbar.searchTip')}
        </div>
      )}
    </header>
  );
}
